import { useEffect, useRef, useState } from "react";
import { Coffee, Check, RotateCcw, X, Send, Info } from "lucide-react";

/**
 * DemoCoffeeChatModal — a fully self-contained, client-side simulated
 * Coffee Chat experience for regular (non-PTA) users on demo projects and
 * demo profiles.
 *
 * IMPORTANT: this component makes ZERO network calls. It touches no real
 * user, no messaging thread, no application row, no analytics, no admin
 * log, no payment record. Nothing here reaches the real Coffee Chat or
 * messaging system.
 *
 * Steps:
 *   1. `intro`    → user sees the notice and writes a sample request
 *   2. `pending`  → simulated 1.5s "sending" animation
 *   3. `accepted` → simulated acceptance with a canned auto-reply
 *   4. `chat`     → practice messaging with a canned demo reply
 *   5. reset / close at any time
 */
const DEMO_NOTICE =
  "Demo / Practice Mode — No real user will receive anything, and no application, message, notification, or database record will be created.";

const CANNED_ACCEPT_MESSAGE =
  "Thanks for reaching out! I'd love to chat about your project. What questions do you have?";

// Small pool of canned replies chosen deterministically so the demo feels
// alive without any real backend.
const CANNED_REPLIES = [
  "That sounds great — I usually take one project at a time so I can give it real attention.",
  "Happy to share a sample of my previous notes so you can see my style before you decide.",
  "My typical turnaround is 2–3 weeks for a full manuscript. Does that work for your timeline?",
  "Great question. I like to flag pacing dips first, then character consistency, then prose.",
  "Absolutely — I always send a wrap-up letter with the top three things that worked and didn't.",
];

function pickCannedReply(count) {
  return CANNED_REPLIES[count % CANNED_REPLIES.length];
}

export function DemoCoffeeChatModal({ open, onClose, context = {} }) {
  const { targetName = "Demo Beta Reader", targetAvatar, sourceLabel } = context;
  const [phase, setPhase] = useState("intro"); // intro | pending | accepted | chat
  const [requestText, setRequestText] = useState(
    "Hi! I loved the sound of your genre focus and would love to chat about a beta read for my next book."
  );
  const [messages, setMessages] = useState([]); // {id, from: 'me'|'them', text, ts}
  const [pendingMsg, setPendingMsg] = useState("");
  const [replyCount, setReplyCount] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset every time the modal is opened fresh.
    setPhase("intro");
    setRequestText(
      "Hi! I loved the sound of your genre focus and would love to chat about a beta read for my next book."
    );
    setMessages([]);
    setPendingMsg("");
    setReplyCount(0);
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, phase]);

  if (!open) return null;

  const send = () => {
    if (phase !== "intro" || !requestText.trim()) return;
    setPhase("pending");
    // Simulated network latency — purely client-side setTimeout.
    setTimeout(() => {
      setPhase("accepted");
      // After a short beat, auto-enter chat mode with the canned welcome
      // message so the user immediately sees the Coffee Chat surface.
      setTimeout(() => {
        setPhase("chat");
        setMessages([
          {
            id: `demo-${Date.now()}-req`,
            from: "me",
            text: requestText.trim(),
            ts: Date.now(),
          },
          {
            id: `demo-${Date.now()}-hello`,
            from: "them",
            text: CANNED_ACCEPT_MESSAGE,
            ts: Date.now() + 400,
          },
        ]);
      }, 900);
    }, 1400);
  };

  const sendChat = () => {
    const text = pendingMsg.trim();
    if (!text) return;
    const myMsg = { id: `demo-${Date.now()}-m`, from: "me", text, ts: Date.now() };
    setMessages((prev) => [...prev, myMsg]);
    setPendingMsg("");
    // Fake a "typing" beat then post a canned reply. Purely client-side.
    const nextIdx = replyCount;
    setReplyCount((c) => c + 1);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: `demo-${Date.now()}-r`,
          from: "them",
          text: pickCannedReply(nextIdx),
          ts: Date.now(),
        },
      ]);
    }, 900);
  };

  const reset = () => {
    setPhase("intro");
    setRequestText(
      "Hi! I loved the sound of your genre focus and would love to chat about a beta read for my next book."
    );
    setMessages([]);
    setPendingMsg("");
    setReplyCount(0);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Demo Coffee Chat"
      data-testid="demo-coffee-modal"
    >
      <div className="w-full sm:max-w-lg bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl border border-border flex flex-col max-h-[92vh]">
        {/* Header + persistent notice */}
        <header className="shrink-0 px-5 pt-5 pb-3 border-b border-border/60 flex items-start gap-3 bg-[hsl(var(--sb-cream-2))]/60">
          <div className="mt-0.5 rounded-full bg-[hsl(var(--sb-clay))]/15 p-2 text-[hsl(var(--sb-clay))]">
            <Coffee size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--sb-clay))] font-semibold"
              data-testid="demo-coffee-eyebrow"
            >
              Demo Coffee Chat · Practice mode
            </p>
            <h2 className="font-serif text-xl leading-tight">
              {sourceLabel ? `Practice with ${sourceLabel}` : `Chat with ${targetName}`}
            </h2>
            <div
              className="mt-2 rounded-xl border border-border bg-[hsl(var(--sb-cream-2))]/70 px-3 py-2.5 text-sm text-foreground leading-relaxed"
              data-testid="demo-coffee-explainer"
            >
              <p className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
                <Coffee size={13} className="text-[hsl(var(--sb-forest))]" /> What's a Coffee Chat?
              </p>
              <p>
                A Coffee Chat is a <strong>private message conversation</strong> for
                discussing the project — asking questions, clarifying expectations
                and availability, and seeing whether it's a good fit. It's{" "}
                <strong>not a scheduled phone or video call.</strong>
              </p>
            </div>
            <p
              className="mt-2 rounded-xl border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/15 px-3 py-2.5 text-sm text-foreground leading-relaxed flex items-start gap-1.5"
              data-testid="demo-coffee-notice"
              role="note"
            >
              <Info size={13} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
              <span><strong className="font-semibold">{DEMO_NOTICE}</strong></span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-muted transition"
            aria-label="Close demo Coffee Chat"
            data-testid="demo-coffee-close-btn"
          >
            <X size={16} />
          </button>
        </header>

        {/* Body — phase machine.
            Intro / pending / accepted share one scroll region so the
            "Your sample request" label + textarea can always scroll
            fully into view above the footer on small mobile screens.
            The chat phase keeps its own layout with internal thread
            scroll + a pinned input row. */}
        {phase === "chat" ? (
          <div className="flex-1 flex flex-col min-h-0">
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-4 space-y-3"
              data-testid="demo-coffee-thread"
            >
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}
                  data-testid={`demo-coffee-msg-${m.from}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.from === "me"
                        ? "bg-[hsl(var(--sb-forest))] text-white rounded-br-sm"
                        : "bg-muted text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="shrink-0 border-t border-border/60 p-3 bg-background">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pendingMsg}
                  onChange={(e) => setPendingMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  placeholder="Type a practice message…"
                  className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/30"
                  data-testid="demo-coffee-input"
                />
                <button
                  type="button"
                  onClick={sendChat}
                  disabled={!pendingMsg.trim()}
                  className="rounded-full bg-[hsl(var(--sb-forest))] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="demo-coffee-send-btn"
                >
                  <Send size={14} /> Send
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="flex-1 min-h-0 overflow-y-auto"
            data-testid="demo-coffee-body-scroll"
          >
            {phase === "intro" && (
              <div
                className="p-5 space-y-3 pb-10"
                data-testid="demo-coffee-intro"
              >
                <label className="block text-sm font-medium">
                  Your sample request
                </label>
                <textarea
                  value={requestText}
                  onChange={(e) => setRequestText(e.target.value)}
                  rows={5}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/30"
                  placeholder="Write a friendly intro…"
                  data-testid="demo-coffee-request-textarea"
                />
                <p className="text-sm text-foreground/80">
                  In a real Coffee Chat this would go to the recipient's inbox. Here it stays entirely in your browser.
                </p>
                {/* Bottom spacer so the last helper line never abuts the sticky footer on iOS. */}
                <div aria-hidden className="h-6" />
              </div>
            )}

            {phase === "pending" && (
              <div
                className="min-h-[40vh] flex flex-col items-center justify-center p-10 text-center"
                data-testid="demo-coffee-pending"
              >
                <div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--sb-forest))]/30 border-t-[hsl(var(--sb-forest))] animate-spin" />
                <p className="mt-4 text-sm text-foreground/80">
                  Sending your practice request…
                </p>
              </div>
            )}

            {phase === "accepted" && (
              <div
                className="min-h-[40vh] flex flex-col items-center justify-center p-10 text-center"
                data-testid="demo-coffee-accepted"
              >
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] flex items-center justify-center">
                  <Check size={22} />
                </div>
                <p className="mt-4 text-sm">
                  <span className="font-semibold">{targetName}</span> accepted your request.
                </p>
                <p className="mt-1 text-sm text-foreground/80">
                  Opening the Coffee Chat…
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer actions */}
        <footer
          className="shrink-0 border-t border-border/60 px-4 py-3 pb-5 sm:pb-3 flex items-center gap-2 bg-[hsl(var(--sb-cream-2))]/40"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}
        >
          {phase === "intro" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-foreground/70 hover:text-foreground hover:underline"
                data-testid="demo-coffee-cancel-btn"
              >
                Cancel
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={send}
                disabled={!requestText.trim()}
                className="rounded-full bg-[hsl(var(--sb-forest))] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="demo-coffee-send-request-btn"
              >
                <Coffee size={14} /> Send practice request
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                className="text-sm font-medium text-foreground/70 hover:text-foreground hover:underline inline-flex items-center gap-1"
                data-testid="demo-coffee-reset-btn"
              >
                <RotateCcw size={13} /> Reset demo
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium"
                data-testid="demo-coffee-end-btn"
              >
                End demo
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

export default DemoCoffeeChatModal;
